import { Card, CardContent, CardHeader, CardTitle } from "@/components/card";
import { numberFormat } from "@/lib/utils";
import { CalendarRange, Coins, Unplug, Users, Zap } from "lucide-react";

interface ClientMetrics {
  activeClientsLastMonth: number;
  activeClientsPreviousMonth: number;
  newClientsCurrentMonth: number;
  newClientsLastMonth: number;
  newClientsHighPerformancePercentage: number;
  churnedLastMonth: number;
  churnedPreviousMonth: number;
}

interface OverviewMetricsCardsProps {
  lastMonthsRevenue: number;
  previousMonthsRevenue: number;
  infrastructureCostShare: number;
  clients: ClientMetrics;
}

const getCpiAchievementColor = (percentage: number): string => {
  if (percentage >= 75) return "text-success";
  if (percentage >= 50) return "text-yellow-600";
  return "text-red-600";
};

export default function OverviewMetricsCards({
  lastMonthsRevenue,
  previousMonthsRevenue,
  infrastructureCostShare,
  clients,
}: OverviewMetricsCardsProps) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Last Month Revenue</CardTitle>
          <CalendarRange />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{numberFormat(lastMonthsRevenue)}</div>
          <p
            className={`text-xs font-normal ${
              lastMonthsRevenue >= previousMonthsRevenue ? "text-success" : "text-fail"
            }`}
          >
            {(lastMonthsRevenue >= previousMonthsRevenue ? "+" : "-") +
              "%" +
              (Math.abs(lastMonthsRevenue / previousMonthsRevenue - 1) * 100).toFixed(2) +
              " from the previous month"}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Infrastructure Cost Share</CardTitle>
          <Coins />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{"%" + (infrastructureCostShare * 100).toFixed(2)}</div>
          <p className="text-xs font-normal text-muted-foreground">Infrastructure share of profit</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Active Clients</CardTitle>
          <Users />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {clients.activeClientsLastMonth}
            <p
              className={`text-xs font-normal ${
                clients.activeClientsLastMonth > clients.activeClientsPreviousMonth
                  ? "text-success"
                  : clients.activeClientsLastMonth === clients.activeClientsPreviousMonth
                    ? "text-muted-foreground"
                    : "text-fail"
              }`}
            >
              {clients.activeClientsPreviousMonth > 0 &&
                (clients.activeClientsLastMonth >= clients.activeClientsPreviousMonth ? "+" : "-") +
                  "%" +
                  (Math.abs(clients.activeClientsLastMonth / clients.activeClientsPreviousMonth - 1) * 100).toFixed(0) +
                  " from the previous month"}
              {clients.activeClientsPreviousMonth === 0 &&
                clients.activeClientsLastMonth > 0 &&
                "New active clients this month"}
              {clients.activeClientsPreviousMonth === 0 && clients.activeClientsLastMonth === 0 && "No active clients"}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">New Clients (Current Month)</CardTitle>
          <Zap />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {clients.newClientsCurrentMonth}
            {clients.newClientsCurrentMonth > 0 && (
              <span className={`ml-2 text-xl ${getCpiAchievementColor(clients.newClientsHighPerformancePercentage)}`}>
                ({clients.newClientsHighPerformancePercentage.toFixed(0)}%) CPI ≤ 200%
              </span>
            )}
            <p
              className={`text-xs font-normal ${
                clients.newClientsCurrentMonth > clients.newClientsLastMonth
                  ? "text-success"
                  : clients.newClientsCurrentMonth === clients.newClientsLastMonth
                    ? "text-muted-foreground"
                    : "text-fail"
              }`}
            >
              {clients.newClientsLastMonth > 0 &&
                (clients.newClientsCurrentMonth >= clients.newClientsLastMonth ? "+" : "-") +
                  "%" +
                  (Math.abs(clients.newClientsCurrentMonth / clients.newClientsLastMonth - 1) * 100).toFixed(0) +
                  " from last month"}
              {clients.newClientsLastMonth === 0 &&
                clients.newClientsCurrentMonth > 0 &&
                "First new clients this month"}
              {clients.newClientsLastMonth === 0 && clients.newClientsCurrentMonth === 0 && "No new clients"}
            </p>
            {clients.newClientsCurrentMonth > 0 && (
              <p className="text-xs font-normal text-muted-foreground mt-1">
                CPI achievement for clients active ≥14 days
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Churned Clients</CardTitle>
          <Unplug />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {clients.churnedLastMonth}
            <p
              className={`text-xs font-normal ${
                clients.churnedLastMonth < clients.churnedPreviousMonth
                  ? "text-success"
                  : clients.churnedLastMonth === clients.churnedPreviousMonth
                    ? "text-muted-foreground"
                    : "text-fail"
              }`}
            >
              {clients.churnedPreviousMonth > 0 &&
                clients.churnedLastMonth > 0 &&
                (clients.churnedLastMonth >= clients.churnedPreviousMonth ? "+" : "-") +
                  "%" +
                  (Math.abs(clients.churnedLastMonth / clients.churnedPreviousMonth - 1) * 100).toFixed(0) +
                  " from the previous month"}
              {clients.churnedPreviousMonth > 0 && clients.churnedLastMonth === 0 && "No churned clients last month"}
              {clients.churnedPreviousMonth === 0 &&
                clients.churnedLastMonth > 0 &&
                "First churned clients this period"}
              {clients.churnedPreviousMonth === 0 && clients.churnedLastMonth === 0 && "No churned clients"}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
